import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import { ScheduleEntryStatus } from "./DetailedItem";

// Extend dayjs with comparison plugins
dayjs.extend(isSameOrAfter);

/**
 * Maps day of week strings to dayjs day numbers (0 = Sunday, 1 = Monday, etc.)
 */
const DAY_OF_WEEK_MAP = {
  SUNDAY: 0,
  MONDAY: 1,
  TUESDAY: 2,
  WEDNESDAY: 3,
  THURSDAY: 4,
  FRIDAY: 5,
  SATURDAY: 6,
};

/**
 * Checks if a given date/time slot is within a stylist's availability
 * @param {Object} stylist - The stylist object containing availability array
 * @param {Date|string} date - The date to check
 * @param {Date|string} time - The specific time to check (optional, if not provided checks whole day)
 * @returns {boolean} - True if stylist is available, false otherwise
 */
export const isStylistAvailable = (stylist, date, time = null) => {
  // If stylist has no availability data, assume they're available (backward compatibility)
  if (!stylist?.availability || !Array.isArray(stylist.availability) || stylist.availability.length === 0) {
    return true;
  }

  const checkDate = dayjs(date);
  const dayOfWeek = checkDate.day(); // 0 = Sunday, 1 = Monday, etc.

  // Find availability for this day of week
  const dayAvailability = stylist.availability.find((avail) => {
    const availDay = DAY_OF_WEEK_MAP[avail.day_of_week];
    return availDay === dayOfWeek;
  });

  // If no availability defined for this day, stylist is not available
  if (!dayAvailability) {
    return false;
  }

  // If we're only checking the day (not specific time), return true if day is available
  if (!time) {
    return true;
  }

  // Check if the specific time is within the availability window
  const checkTime = dayjs(time);
  
  // Parse the start and end times from availability (format: "HH:mm:ss")
  const [startHour, startMinute] = dayAvailability.start_time.split(':').map(Number);
  const [endHour, endMinute] = dayAvailability.end_time.split(':').map(Number);
  
  // Create dayjs objects for comparison (same date, different times)
  const availabilityStart = checkDate.hour(startHour).minute(startMinute).second(0);
  const availabilityEnd = checkDate.hour(endHour).minute(endMinute).second(0);
  
  // Check if the time falls within the availability window
  return checkTime.isSameOrAfter(availabilityStart) && checkTime.isBefore(availabilityEnd);
};

export const STYLIST_AVAILABILITY_ERROR =
  "The selected time is outside the stylist's available hours. Please choose a different time.";

/** False for 0-minute finishing markers — they must not occupy the stylist. */
export function isBlockingTimeRange(start, end) {
  const s = new Date(start).getTime();
  const e = new Date(end).getTime();
  return Number.isFinite(s) && Number.isFinite(e) && e > s;
}

export function isZeroDurationEvent(item) {
  if (item?.isZeroDuration) return true;
  const start = item?.services?.[0]?.serviceStart ?? item?.start;
  const end = item?.services?.[0]?.serviceEnd ?? item?.end;
  return !isBlockingTimeRange(start, end);
}

/**
 * Events drawn on the scheduler grid.
 * 0-minute finishing blocks stay in appointment details.
 * Cancelled appointments stay in the client's history, not on the grid.
 */
export function visibleSchedulerEvents(events) {
  return (events || []).filter(
    (event) =>
      !isZeroDurationEvent(event) &&
      event?.appointment_status !== ScheduleEntryStatus.Canceled
  );
}

function getAvailabilityEnd(stylist, date) {
  const dayAvailability = getStylistDayAvailability(stylist, date);
  if (!dayAvailability) {
    return null;
  }

  const checkDate = dayjs(date);
  const [endHour, endMinute] = dayAvailability.end_time.split(":").map(Number);
  return checkDate.hour(endHour).minute(endMinute).second(0);
}

/**
 * Builds booked segment windows from service step durations (same logic as CustomEditor).
 * @returns {{ busyTimes: { start: import("dayjs").Dayjs, end: import("dayjs").Dayjs }[], appointmentEnd: import("dayjs").Dayjs }}
 */
export function buildServiceBusyTimes(scheduleStart, services, serviceSteps) {
  let cursor = dayjs(scheduleStart);
  const busyTimes = [];

  for (const service of services) {
    const stepDetails = serviceSteps.find(
      (step) => step.service_id.toString() === service.service_id.toString()
    );
    if (!stepDetails) {
      continue;
    }

    const step1End = cursor.add(
      parseInt(stepDetails.service_step1_minutes, 10),
      "minute"
    );
    busyTimes.push({ start: cursor, end: step1End });

    cursor = step1End.add(
      parseInt(stepDetails.service_step2_minutes, 10) || 0,
      "minute"
    );

    if (stepDetails.service_step3_minutes) {
      const step3Minutes =
        parseInt(stepDetails.service_step3_minutes, 10) || 0;
      const step3End = cursor.add(step3Minutes, "minute");
      if (isBlockingTimeRange(cursor, step3End)) {
        busyTimes.push({ start: cursor, end: step3End });
      }
      cursor = step3End;
    }
  }

  return { busyTimes, appointmentEnd: cursor };
}

/**
 * Validates that an appointment start and end fit within stylist availability.
 * Reuses isStylistAvailable for the start-time check used when editing appointments.
 */
export function validateStylistAvailabilityForAppointment(stylist, start, end) {
  if (!stylist) {
    return { valid: true };
  }

  if (!isStylistAvailable(stylist, start, start)) {
    return { valid: false, message: STYLIST_AVAILABILITY_ERROR };
  }

  const availabilityEnd = getAvailabilityEnd(stylist, start);
  if (availabilityEnd && dayjs(end).isAfter(availabilityEnd)) {
    return { valid: false, message: STYLIST_AVAILABILITY_ERROR };
  }

  return { valid: true };
}

/**
 * Gets the availability for a stylist on a specific day
 * @param {Object} stylist - The stylist object containing availability array
 * @param {Date|string} date - The date to check
 * @returns {Object|null} - The availability object for that day, or null if not available
 */
export const getStylistDayAvailability = (stylist, date) => {
  if (!stylist?.availability || !Array.isArray(stylist.availability) || stylist.availability.length === 0) {
    return null;
  }

  const checkDate = dayjs(date);
  const dayOfWeek = checkDate.day();

  return stylist.availability.find((avail) => {
    const availDay = DAY_OF_WEEK_MAP[avail.day_of_week];
    return availDay === dayOfWeek;
  }) || null;
};

/**
 * Checks if a time slot (cell) should be greyed out for a stylist
 * This is the main function to use for cell rendering
 * @param {Object} stylist - The stylist object
 * @param {Date} cellDate - The date/time of the cell
 * @returns {boolean} - True if cell should be greyed out (unavailable)
 */
export const isCellUnavailable = (stylist, cellDate) => {
  return !isStylistAvailable(stylist, cellDate, cellDate);
};
